import {Link} from 'react-router-dom';

import Avatar from 'src/components/Avatar';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {useCurrentOrganization} from 'src/hooks/useAuth';
import {ApiOrganization, ApiUserProfile} from 'src/lib/api/auth';
import {makeUuidPrefix} from 'src/lib/routeUtils';
import * as tw from 'src/lib/tailwindClasses';

type Props = {
  currentUser: ApiUserProfile;
  users: ApiUserProfile[];
  organization: ApiOrganization;
};

export default function Users({currentUser, users, organization}: Props) {
  const {t} = useTranslations();
  const orgUuidPrefix = makeUuidPrefix(organization.id);
  const currentOrg = useCurrentOrganization()!;
  return (
    <div className="relative">
      <PageTitle
        pageStack={[
          {title: currentOrg.name, to: '/:currentOrgIdPrefix/projects'},
          {title: t('settings'), to: '/:currentOrgIdPrefix/settings'},
          {title: t('team'), to: '/:currentOrgIdPrefix/settings/users'},
        ]}
      />
      {currentUser.role !== 'readonly' && (
        <Link to={`/${orgUuidPrefix}/settings/invite`} className="absolute right-0 top-0">
          <button className={`${tw.buttonPrimary}`}>{t('inviteUser')}</button>
        </Link>
      )}
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t('name')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t('status')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t('role')}
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">{t('edit')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => {
                  return (
                    <tr key={user.email}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <Avatar className="h-10 w-10 rounded-full" user={user} />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {t('active')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {t(`${user.role}Role`)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(currentUser.role === 'owner' ||
                          currentUser.role === 'admin' ||
                          currentUser.id === user.id) && (
                          <Link
                            to={`/${orgUuidPrefix}/settings/users/${user.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t('edit')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
